"use client"

import { Popover, PopoverPanel, Transition } from "@headlessui/react"
import { ArrowRightMini, XMark } from "@medusajs/icons"
import { clx, useToggleState } from "@medusajs/ui"
import { Fragment } from "react"

import LocalizedClientLink from "@modules/common/components/localized-client-link"
import CountrySelect from "../country-select"
import LanguageSelect from "../language-select"
import { HttpTypes } from "@medusajs/types"
import { Locale } from "@lib/data/locales"

const SideMenuItems = {
  Home: "/",
  Store: "/store",
  Account: "/account",
  Cart: "/cart",
}

const labelsCs: Record<string, string> = {
  Home: "Domů",
  Store: "Obchod",
  Account: "Účet",
  Cart: "Košík",
}

type SideMenuProps = {
  regions: HttpTypes.StoreRegion[] | null
  locales: Locale[] | null
  currentLocale: string | null
}

const SideMenu = ({ regions, locales, currentLocale }: SideMenuProps) => {
  const countryToggleState = useToggleState()
  const languageToggleState = useToggleState()

  return (
    <div className="h-full">
      <div className="flex h-full items-center">
        <Popover className="flex h-full">
          {({ open, close }) => (
            <>
              <div className="relative flex h-full">
                <Popover.Button
                  data-testid="nav-menu-button"
                  aria-label="Otevřít menu"
                  className="relative flex h-full items-center transition-colors duration-200 hover:text-brand-accent focus:outline-none focus-visible:text-brand-accent"
                >
                  Menu
                </Popover.Button>
              </div>

              {open && (
                <div
                  className="fixed inset-0 z-[50] bg-[rgba(17,17,17,0.28)] backdrop-blur-[2px]"
                  onClick={close}
                  data-testid="side-menu-backdrop"
                />
              )}

              <Transition
                show={open}
                as={Fragment}
                enter="transition duration-200 ease-out"
                enterFrom="opacity-0 -translate-x-3"
                enterTo="opacity-100 translate-x-0"
                leave="transition duration-150 ease-in"
                leaveFrom="opacity-100 translate-x-0"
                leaveTo="opacity-0 -translate-x-3"
              >
                <PopoverPanel className="fixed inset-y-2 left-2 z-[51] flex h-[calc(100vh-1rem)] w-[calc(100%-1rem)] max-w-[30rem] flex-col text-white">
                  <div
                    data-testid="nav-menu-popup"
                    className="shk-nav-popup relative flex h-full flex-col overflow-y-auto bg-[var(--shk-ink)] p-5 shadow-[6px_6px_0_var(--shk-primary)] sm:p-8"
                  >
                    <div className="pointer-events-none absolute right-0 top-0 h-24 w-24 bg-[var(--shk-bauhaus-yellow)] sm:h-32 sm:w-32" />

                    <div className="relative flex items-start justify-between gap-6">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--shk-bauhaus-yellow)]">
                          The Brutalist
                        </p>
                        <p className="mt-2 text-sm text-white/60">Navigace</p>
                      </div>

                      <button
                        type="button"
                        data-testid="close-menu-button"
                        aria-label="Zavřít menu"
                        onClick={close}
                        className="group relative z-10 flex h-11 w-11 shrink-0 items-center justify-center border border-white/40 text-white transition-colors duration-200 hover:border-[var(--shk-primary)] hover:bg-[var(--shk-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shk-bauhaus-yellow)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--shk-ink)]"
                      >
                        <XMark className="h-5 w-5 transition-transform duration-200 group-hover:rotate-90" />
                      </button>
                    </div>

                    <nav aria-label="Hlavní navigace" className="relative my-12 flex-1">
                      <ul className="flex flex-col">
                        {Object.entries(SideMenuItems).map(([name, href], index) => (
                          <li key={name} className="border-b border-white/20 first:border-t">
                            <LocalizedClientLink
                              href={href}
                              className="group flex items-baseline gap-4 py-4 text-3xl font-medium leading-none tracking-tight text-white transition-colors duration-200 hover:text-[var(--shk-bauhaus-yellow)] focus:outline-none focus-visible:text-[var(--shk-bauhaus-yellow)] sm:py-5 sm:text-5xl"
                              onClick={close}
                              data-testid={`${name.toLowerCase()}-link`}
                            >
                              <span className="w-6 text-xs font-bold tracking-normal text-[var(--shk-primary)] transition-colors group-hover:text-[var(--shk-bauhaus-yellow)] sm:text-sm">
                                {String(index + 1).padStart(2, "0")}
                              </span>
                              <span>{labelsCs[name] ?? name}</span>
                            </LocalizedClientLink>
                          </li>
                        ))}
                      </ul>
                    </nav>

                    <div className="relative flex flex-col gap-4 border-t border-white/20 pt-5 text-sm">
                      {!!locales?.length && (
                        <div
                          className="flex items-center justify-between border-b border-white/20 pb-4 text-white"
                          onMouseEnter={languageToggleState.open}
                          onMouseLeave={languageToggleState.close}
                        >
                          <LanguageSelect
                            toggleState={languageToggleState}
                            locales={locales}
                            currentLocale={currentLocale}
                          />
                          <ArrowRightMini
                            className={clx(
                              "h-5 w-5 transition-transform duration-150",
                              languageToggleState.state ? "-rotate-90" : ""
                            )}
                          />
                        </div>
                      )}

                      <div
                        className="flex items-center justify-between border-b border-white/20 pb-4 text-white"
                        onMouseEnter={countryToggleState.open}
                        onMouseLeave={countryToggleState.close}
                      >
                        {regions && (
                          <CountrySelect
                            toggleState={countryToggleState}
                            regions={regions}
                          />
                        )}
                        <ArrowRightMini
                          className={clx(
                            "h-5 w-5 transition-transform duration-150",
                            countryToggleState.state ? "-rotate-90" : ""
                          )}
                        />
                      </div>

                      <p className="pt-2 text-xs text-white/50">
                        © {new Date().getFullYear()}. Všechna práva vyhrazena.
                      </p>
                    </div>
                  </div>
                </PopoverPanel>
              </Transition>
            </>
          )}
        </Popover>
      </div>
    </div>
  )
}

export default SideMenu
